import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import Can from '../../../components/permissions/Can';

export default function TaskCommentsTab({
  comments,
  currentUserId,
  isEditorHtmlEmpty,
  formatCommentHtmlForDisplay,
  handleCommentContentClick,
  attachmentTypeIcon,
  handleDownloadAttachment,
  commentFileInputRef,
  handleCommentFileInputChange,
  commentDragActive,
  setCommentDragActive,
  handleCommentEditorDrop,
  commentToolbarActions,
  handleCommentToolbarAction,
  commentEditorRef,
  syncCommentEditorState,
  handleCommentImagePaste,
  saveCommentSelection,
  commentAttachedFiles,
  handleCommentAttachmentRemove,
  commentUploadingImage,
  commentSubmitting,
  handleCommentSubmit,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
        {comments.length === 0 ? (
          <Alert severity="info">No comments yet.</Alert>
        ) : (
          comments.map((comment, index) => {
            const commentAuthorId = comment?.author?.id ?? comment?.author_id ?? '';
            const isLoginUserComment =
              String(commentAuthorId || '') !== '' && String(commentAuthorId) === String(currentUserId ?? '');
            const commentBody = comment?.comment ?? comment?.body ?? comment?.message ?? '';
            const hasCommentBody = !isEditorHtmlEmpty(commentBody);

            return (
              <Box
                key={comment?.id ?? index}
                sx={{
                  display: 'flex',
                  width: '100%',
                  gap: 1.5,
                  mb: 1.5,
                  justifyContent: isLoginUserComment ? 'flex-end' : 'flex-start',
                }}
              >
                {!isLoginUserComment ? (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'grey.500',
                      flexShrink: 0,
                      mt: 0.75,
                    }}
                  />
                ) : null}
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2.5,
                    bgcolor: isLoginUserComment ? '#f4f7fb' : '#eef6ff',
                    borderColor: isLoginUserComment ? 'divider' : 'primary.light',
                    width: 'fit-content',
                    maxWidth: 'calc(100% - 40px)',
                    minWidth: { xs: 'unset', sm: 260 },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'primary.main', textAlign: isLoginUserComment ? 'right' : 'left' }}
                  >
                    {comment?.author?.name ?? comment?.author?.username ?? 'Unknown'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      textAlign: isLoginUserComment ? 'right' : 'left',
                    }}
                  >
                    {comment?.created_at ?? '—'}
                  </Typography>
                  {hasCommentBody ? (
                    <Box
                      onClick={(event) => handleCommentContentClick(comment, event)}
                      sx={{
                        mt: 0.5,
                        fontSize: 14,
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        '& p': { my: 0.5 },
                        '& img': {
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: 1,
                          display: 'block',
                          my: 1,
                          cursor: 'pointer',
                        },
                        '& a': { color: 'primary.main' },
                        '& blockquote': {
                          m: 0,
                          pl: 1.5,
                          borderLeft: '3px solid',
                          borderColor: 'divider',
                          color: 'text.secondary',
                        },
                        '& code': {
                          px: 0.5,
                          py: 0.15,
                          borderRadius: 0.5,
                          bgcolor: 'grey.100',
                          fontFamily: 'monospace',
                        },
                        '& ul, & ol': { pl: 2.5, my: 0.75 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatCommentHtmlForDisplay(commentBody),
                      }}
                    />
                  ) : null}
                  {Array.isArray(comment?.attachments) && comment.attachments.length > 0 ? (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: isLoginUserComment ? 'flex-end' : 'flex-start',
                      }}
                    >
                      {comment.attachments.map((file, fileIndex) => (
                        <Chip
                          key={file?.id ?? fileIndex}
                          size="small"
                          variant="outlined"
                          icon={attachmentTypeIcon(file)}
                          label={file?.document_name ?? file?.file_name ?? file?.title ?? 'Attachment'}
                          onClick={() => handleDownloadAttachment(file)}
                          clickable
                        />
                      ))}
                    </Box>
                  ) : null}
                </Paper>
                {isLoginUserComment ? (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'grey.500',
                      flexShrink: 0,
                      mt: 0.75,
                    }}
                  />
                ) : null}
              </Box>
            );
          })
        )}
      </Box>

      <Can permission="task-comment-create">
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
        <input
          ref={commentFileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleCommentFileInputChange}
        />
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            borderRadius: 1,
            bgcolor: '#fff',
            borderColor: commentDragActive ? 'primary.main' : '#d6d9de',
            borderStyle: commentDragActive ? 'dashed' : 'solid',
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setCommentDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setCommentDragActive(false);
          }}
          onDrop={handleCommentEditorDrop}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              px: 0.75,
              py: 0.4,
              minHeight: 42,
              bgcolor: '#f3f4f6',
              borderBottom: '1px solid',
              borderColor: '#d6d9de',
            }}
          >
            {commentToolbarActions.map(({ key, icon: Icon }) => (
              <IconButton
                key={key}
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleCommentToolbarAction(key)}
                sx={{ color: 'text.secondary' }}
              >
                <Icon fontSize="small" />
              </IconButton>
            ))}
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commentFileInputRef.current?.click()}
              sx={{ color: 'text.secondary' }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
            <Box
              sx={{
                flex: 1,
                height: 12,
                ml: 1,
                bgcolor: '#d9dde2',
                borderRadius: 0.5,
              }}
            />
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleCommentToolbarAction('undo')}
              sx={{ color: 'text.secondary' }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleCommentToolbarAction('redo')}
              sx={{ color: 'text.secondary' }}
            >
              <RedoIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            ref={commentEditorRef}
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Write a comment"
            onInput={syncCommentEditorState}
            onPaste={handleCommentImagePaste}
            onMouseUp={saveCommentSelection}
            onKeyUp={saveCommentSelection}
            onBlur={() => {
              saveCommentSelection();
              syncCommentEditorState();
            }}
            sx={{
              px: 2,
              py: 1,
              fontSize: 15,
              minHeight: 96,
              maxHeight: 180,
              overflowY: 'auto',
              outline: 'none',
              cursor: 'text',
              '&[data-placeholder]:empty:before': {
                content: 'attr(data-placeholder)',
                color: 'text.disabled',
              },
              '& p': { my: 0.5 },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                display: 'block',
                my: 1,
              },
              '& a': { color: 'primary.main' },
              '& blockquote': {
                m: 0,
                pl: 1.5,
                borderLeft: '3px solid',
                borderColor: 'divider',
                color: 'text.secondary',
              },
              '& code': {
                px: 0.5,
                py: 0.15,
                borderRadius: 0.5,
                bgcolor: 'grey.100',
                fontFamily: 'monospace',
              },
              '& ul, & ol': { pl: 2.5, my: 0.75 },
            }}
          />
          {commentAttachedFiles.length > 0 ? (
            <Box
              sx={{
                px: 2,
                pb: 1.25,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {commentAttachedFiles.map((file, index) => (
                <Chip
                  key={file?.id ?? index}
                  size="small"
                  variant="outlined"
                  icon={attachmentTypeIcon(file)}
                  label={file?.document_name ?? file?.file_name ?? file?.title ?? 'Attachment'}
                  onClick={() => handleDownloadAttachment(file)}
                  onDelete={() => handleCommentAttachmentRemove(file)}
                />
              ))}
            </Box>
          ) : null}
        </Paper>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel control={<Checkbox size="small" />} label="Sensitive Comment" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {commentUploadingImage ? (
              <Typography variant="caption" color="text.secondary">
                Uploading files...
              </Typography>
            ) : null}
            {!commentUploadingImage ? (
              <Typography variant="caption" color="text.secondary">
                Drag and drop files here or use the attach button.
              </Typography>
            ) : null}
            <Button
              variant="contained"
              onClick={handleCommentSubmit}
              disabled={commentSubmitting || commentUploadingImage}
              sx={{ minWidth: 120 }}
            >
              {commentSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Send'}
            </Button>
          </Box>
        </Box>
      </Box>
      </Can>
    </Box>
  );
}
